/**
 * saveFailure — how a failed cloud save is reported to the user.
 *
 * The editor used to report EVERY failed save as "Offline — kept locally, will
 * sync". That is a lie whenever the server actually answered: a 401 (expired
 * session), a 404 (deleted file) or a 500 all left the user staring at a calm
 * "will sync" chip while nothing ever synced. It hid a real, total save outage
 * for days.
 *
 * The rule, in one line: NEVER claim "offline" when the server answered.
 *
 *  - the browser says it is offline, or the request never reached a server
 *    (fetch threw: DNS, connection refused, aborted)  → "offline"
 *  - the server responded with an error status                → "error", and
 *    after repeated consecutive failures                      → "failed"
 *
 * Either way the work is kept in the per-file local buffer and retried with
 * backoff; only the wording (and the honesty) differ.
 */

/** Where a failed save died: below the HTTP layer, or at the server. */
export type SaveFailureKind = "network" | "server";

export type SaveFailureStatus = "offline" | "error" | "failed";

/** Consecutive server-side failures before we stop promising a retry will fix it. */
export const FAILED_AFTER_CONSECUTIVE = 3;

export const classifySaveFailure = ({
  kind,
  isOnline,
  consecutiveFailures,
}: {
  kind: SaveFailureKind;
  /** `navigator.onLine` at the moment the save failed. */
  isOnline: boolean;
  /** Count including the failure being classified (first failure = 1). */
  consecutiveFailures: number;
}): SaveFailureStatus => {
  if (!isOnline || kind === "network") {
    return "offline";
  }
  // The server answered — this is a real error, not connectivity.
  return consecutiveFailures >= FAILED_AFTER_CONSECUTIVE ? "failed" : "error";
};

/** `navigator.onLine`, defensively (jsdom/node may not define navigator). */
export const isBrowserOnline = (): boolean =>
  typeof navigator === "undefined" || navigator.onLine !== false;
